import { useState, useEffect, useCallback, useRef } from 'react'
import {
  loadHazardEvents,
  distanceKm,
  inPAR,
  inStormWatchZone,
  isParRelevantStorm,
  type HazardEvent,
  type HazardAlertLevel,
} from '@davids-beacon/safety-core'
import { proxyUrl } from './proxyBase'

// All fetch/parse/dedupe/staleness logic lives in @davids-beacon/safety-core
// (packages/safety-core/src/utils/hazards.ts) — shared with the RN app.
// This file is the web adapter: caching, React hook, and web-only UI metadata.

// ── Re-exported shared types & helpers ───────────────────────────────────────

export type AlertLevel = HazardAlertLevel
export type DisasterEvent = HazardEvent
export { distanceKm, inPAR, inStormWatchZone, isParRelevantStorm }

export type HazardScope = 'nearby' | 'all'

export interface DisasterRegion {
  label: string
  lat: number
  lng: number
  radiusKm: number
}

// ── Category metadata (web styling) ──────────────────────────────────────────

export const CATEGORY_META: Record<string, { label: string; icon: string; color: string; mapColor: string }> = {
  severeStorms: { label: 'Storms',      icon: 'fa-cloud-bolt',       color: 'text-blue-600',    mapColor: '#2563eb' },
  floods:       { label: 'Floods',      icon: 'fa-water',            color: 'text-cyan-600',    mapColor: '#0891b2' },
  wildfires:    { label: 'Wildfires',   icon: 'fa-fire',             color: 'text-orange-600',  mapColor: '#ea580c' },
  earthquakes:  { label: 'Earthquakes', icon: 'fa-circle-radiation', color: 'text-red-600',     mapColor: '#dc2626' },
  volcanoes:    { label: 'Volcanoes',   icon: 'fa-volcano',          color: 'text-rose-700',    mapColor: '#be123c' },
  tsunami:      { label: 'Tsunami',     icon: 'fa-wave-square',      color: 'text-blue-700',    mapColor: '#1d4ed8' },
  snow:         { label: 'Snow',        icon: 'fa-snowflake',        color: 'text-sky-500',     mapColor: '#0ea5e9' },
  tempExtremes: { label: 'Heat/Cold',   icon: 'fa-temperature-half', color: 'text-red-500',     mapColor: '#ef4444' },
  dustHaze:     { label: 'Dust/Haze',   icon: 'fa-smog',             color: 'text-slate-500',    mapColor: '#6b7280' },
  manmade:      { label: 'Manmade',     icon: 'fa-industry',         color: 'text-slate-700',    mapColor: '#374151' },
}

export const ALL_CATEGORIES = Object.keys(CATEGORY_META)

// ── Reverse geocoding cache (module-level, persists across renders) ──────────

const geocodeCache = new Map<string, string>()

export async function reverseGeocode(lat: number, lng: number, signal?: AbortSignal): Promise<string | null> {
  const key = `${lat.toFixed(3)},${lng.toFixed(3)}`
  if (geocodeCache.has(key)) return geocodeCache.get(key)!
  try {
    const res = await window.fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`,
      { headers: { 'Accept-Language': 'en' }, signal },
    )
    if (!res.ok) return null
    const data = (await res.json()) as { address?: Record<string, string> }
    const a = data.address ?? {}
    const city = a.city || a.town || a.municipality || a.suburb || a.village || a.county || ''
    const region = a.state || a.province || a.region || ''
    const country = a.country || ''
    const label = [city, region, country].filter(Boolean).join(', ')
    if (label) geocodeCache.set(key, label)
    return label || null
  } catch {
    return null
  }
}

// ── Event cache ───────────────────────────────────────────────────────────────

const CACHE_TTL_MS = 60 * 60 * 1000
const eventCache = new Map<string, { events: DisasterEvent[]; ts: number }>()

function cacheKey(scope: HazardScope, region: DisasterRegion | null, days: number): string {
  if (scope === 'all' || !region) return `all:${days}`
  return `nearby:${Math.round(region.lat * 10)}:${Math.round(region.lng * 10)}:${region.radiusKm}:${days}`
}

// ── Main hook ─────────────────────────────────────────────────────────────────

interface UseDisasterAlertsOptions {
  region: DisasterRegion | null
  scope: HazardScope
  days?: number
}

interface UseDisasterAlertsResult {
  events: DisasterEvent[]
  loading: boolean
  error: string | null
  refresh: () => void
}

const levelOrder: Record<string, number> = { red: 0, orange: 1, green: 2, '': 3 }

function sortEvents(evts: DisasterEvent[], region: DisasterRegion | null): DisasterEvent[] {
  return [...evts].sort((a, b) => {
    const la = levelOrder[a.alertLevel ?? ''] ?? 3
    const lb = levelOrder[b.alertLevel ?? ''] ?? 3
    if (la !== lb) return la - lb
    if (region && a.point && b.point) {
      const da = distanceKm(region.lat, region.lng, a.point[0], a.point[1])
      const db = distanceKm(region.lat, region.lng, b.point[0], b.point[1])
      if (Math.abs(da - db) > 50) return da - db
    }
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })
}

/** Nearby scope: keep events within radius; storms also kept when PAR-relevant. */
function applyScopeFilter(evts: DisasterEvent[], scope: HazardScope, region: DisasterRegion | null): DisasterEvent[] {
  if (scope !== 'nearby' || !region) return evts
  return evts.filter(e => {
    if (!e.point) return true
    if (e.category === 'severeStorms' && isParRelevantStorm(e)) return true
    return distanceKm(region.lat, region.lng, e.point[0], e.point[1]) <= region.radiusKm
  })
}

export function useDisasterAlerts({
  region,
  scope,
  days = 30,
}: UseDisasterAlertsOptions): UseDisasterAlertsResult {
  const key = cacheKey(scope, region, days)
  const cached = eventCache.get(key)
  const isFresh = cached !== undefined && Date.now() - cached.ts < CACHE_TTL_MS

  const [events, setEvents] = useState<DisasterEvent[]>(isFresh ? cached.events : [])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const fetchEvents = useCallback(async (force = false) => {
    const k = cacheKey(scope, region, days)
    const hit = eventCache.get(k)
    if (!force && hit && Date.now() - hit.ts < CACHE_TTL_MS) {
      setEvents(hit.events)
      return
    }

    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)

    try {
      const regionCountry = region
        ? await reverseGeocode(region.lat, region.lng).catch(() => null)
        : null

      const { events: fetched, failures } = await loadHazardEvents({
        proxyBase: proxyUrl,
        lat: region?.lat ?? null,
        lng: region?.lng ?? null,
        radiusKm: scope === 'all' ? 10000 : (region?.radiusKm ?? 1000),
        days,
        country: regionCountry,
      }, ctrl.signal)

      if (ctrl.signal.aborted) return

      const scoped = sortEvents(applyScopeFilter(fetched, scope, region), region)
      setEvents(scoped)
      eventCache.set(k, { events: scoped, ts: Date.now() })

      if (failures.includes('gdacs') && failures.includes('usgs')) {
        setError('Could not load hazard data. Check your connection.')
      }
    } catch (e: unknown) {
      if ((e as Error)?.name !== 'AbortError') {
        setError('Could not load hazard data. Check your connection.')
      }
    } finally {
      setLoading(false)
    }
  }, [region, scope, days])

  useEffect(() => {
    void fetchEvents()
    return () => { abortRef.current?.abort() }
  }, [fetchEvents])

  return { events, loading, error, refresh: () => void fetchEvents(true) }
}

// ── Location label helper ─────────────────────────────────────────────────────

// GDACS/USGS/Tomorrow.io all carry location text; no reverse-geocoding needed.
export function useEventLocationLabels(_events: DisasterEvent[]): Record<string, string> {
  return {}
}

// ── Stored scope ──────────────────────────────────────────────────────────────

const SCOPE_KEY = 'hazards_scope'

export function getStoredScope(): HazardScope {
  return (localStorage.getItem(SCOPE_KEY) as HazardScope) ?? 'nearby'
}

export function setStoredScope(scope: HazardScope) {
  localStorage.setItem(SCOPE_KEY, scope)
}

// ── Stored preferred categories ───────────────────────────────────────────────

const PREF_CATS_KEY = 'hazards_pref_cats'

export function getStoredPrefCats(): string[] {
  try {
    const raw = localStorage.getItem(PREF_CATS_KEY)
    const cats = raw ? (JSON.parse(raw) as string[]) : []
    // Drop categories that no longer exist (e.g. 'drought' persisted from an
    // older build) so removed categories can't ghost back in from storage.
    return cats.filter(c => c in CATEGORY_META)
  } catch { return [] }
}

export function setStoredPrefCats(cats: string[]) {
  localStorage.setItem(PREF_CATS_KEY, JSON.stringify(cats))
}
